import React, { useEffect, useState, useCallback, useRef } from "react";
import Breadcrumbs from "../../layout-components/Breadcrumbs";
import DataTable, { Column } from "../../layout-components/DataTable";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { useBusiness } from "../../contexts/BusinessContext";
import { Form, Row, Col, Button } from "react-bootstrap";
import { BookingViewModal, BookingViewModalRef } from "./BookingViewModal";

export default function BookingList() {
  const navigate = useNavigate();
  const { business } = useBusiness();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [statusFilter, setStatusFilter] = useState(0); // 0: All
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination States matching Laravel response metadata
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);

  // Reference to the external fixed Modal component
  const viewModalRef = useRef<BookingViewModalRef>(null);

  const fetchBookings = useCallback(async (pageNumber = 1) => {
    if (!business?.id) return;

    try {
      setLoading(true);
      const reqBody = {
        page: pageNumber,
        per_page: perPage,
        business_id: business.id,
        status: statusFilter,
        search: searchTerm,
      };

      const response = await _post(apiUrls.getBookingList, reqBody);

      // 1. Get the root JSON payload (handles wrapper variants)
      const rootJson = response.data?.success !== undefined ? response.data : response;

      // 2. Extract the pagination block wrapper
      const paginationData = rootJson?.data; // This holds { current_page: 1, data: [...] }

      // 3. Extract the actual booking array and safely set the states
      const bookingArray = paginationData?.data || [];

      setData(bookingArray);
      setCurrentPage(paginationData?.current_page || 1);
      setLastPage(paginationData?.last_page || 1);
      setPerPage(paginationData?.per_page || 20); // Sync fallback parameter setup
      setTotalItems(paginationData?.total || 0);

    } catch (err) {
      console.error("Failed to fetch bookings", err);
      setData([]); // Gracefully clear on failure
    } finally {
      setLoading(false);
    }
  }, [business?.id, statusFilter, searchTerm, perPage]);

  // Handle page changes gracefully
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchBookings(page);
    }
  };

  // Re-run safely whenever business details or filtering selectors are toggled
  useEffect(() => {
    fetchBookings(1);
  }, [business?.id, statusFilter]);

  // Columns specification mapping configuration layout
  const columns: Column<any>[] = [
    { 
      key: "id", 
      label: "SL",
      render: (item: any, index?: number) => (
        <span className="fw-medium text-muted">
          {((currentPage - 1) * perPage) + (index ?? 0) + 1}
        </span>
      )
    },
    {
      key: "customer_name",
      label: "Customer Info",
      render: (item: any) => (
        <div>
          <div className="fw-bold text-dark">{item.customer_name}</div>
          <small className="text-muted">{item.mobile_no || "No Contact"}</small>
        </div>
      )
    },
    {
      key: "service_names",
      label: "Requested Services",
      render: (item: any) => (
        <div style={{ maxWidth: "220px" }}>
          <span className="fw-medium text-dark text-truncate d-block" title={item.service_names}>
            {item.service_names || <span className="text-muted italic small">No services tracked</span>}
          </span>
        </div>
      )
    },
    {
      key: "booking_type",
      label: "Classification",
      render: (item: any) => (
        <span className={`${item.booking_type === 'instant' ? 'text-danger' : 'text-primary'} text-uppercase fw-bold small`}>
          {item.booking_type || ""}
        </span>
      )
    },
    {
      key: "location_type",
      label: "Location Type",
      render: (item: any) => (
        <span className={`${item.location_type === 'center' ? 'text-primary' : 'text-dark'} text-uppercase fw-bold small`}>
          {item.location_type === 'home' ? '🏠 Home / Other' : '🏢 Center'}
        </span>
      )
    },
    {
      key: "total_payable",
      label: "Total Payable",
      render: (item: any) => (
        <span className="fw-bold text-primary">
          {item.total_payable ? `${item.total_payable} BDT` : "0 BDT"}
        </span>
      )
    },
    {
      key: "scheduled_at",
      label: "Timeline Plan",
      render: (item: any) => {
        if (!item.scheduled_at) return <span className="text-muted">N/A</span>;
        const date = new Date(item.scheduled_at);
        return (
          <div>
            <div className="text-dark small fw-medium">
              {date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <small className="text-muted">
              {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </small>
          </div>
        );
      }
    },
  ];

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">Booking List</h1>

      <div className="row">
        <div className="col-12">
          {/* --- SEARCH & FILTER SECTION --- */}
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <Row className="align-items-end g-3">
            <Col md={5}>
              <Form.Label className="fw-bold text-secondary">Search Customer</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchBookings(1)}
              />
            </Col>
            <Col md={4} >
              <Form.Label className="fw-bold text-secondary">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(Number(e.target.value))}
              >
                <option value={0}>All Bookings</option>
                <option value={1}>Pending</option>
                <option value={2}>Confirmed</option>
                <option value={3}>Completed</option>
                <option value={4}>Cancelled</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button variant="primary" className="w-100 fw-medium" onClick={() => fetchBookings(1)}>
                <FeatherIcon icon="sliders" size={15} className="me-2" /> Adjust View
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
          <h5 className="card-title mb-0 fw-bold text-dark">Booking List</h5>
          <button className="btn btn-primary btn-sm fw-medium px-3" onClick={() => navigate("/bookings/new")}>
            <FeatherIcon icon="plus" size={15} className="me-1" /> New Booking
          </button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary spinner-border-sm"></div></div>
          ) : (
            <>
              {/* --- DYNAMIC DATATABLE INGESTION CONTAINER --- */}
              <DataTable
                data={data}
                columns={columns}
                onView={(item) => viewModalRef.current?.open(item)}
                onEdit={(item) => navigate(`/bookings/edit/${item.id}`)}
                onDelete={(item) => console.log("Delete Request Captured:", item.id)}
              />

              {/* --- PAGINATION FOOTER CONTROL --- */}
              {lastPage > 1 && (
                <div className="card-footer bg-white d-flex justify-content-between align-items-center px-4 py-3 border-top">
                  <div className="">
                    Showing entries {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} entries
                  </div>

                  <nav aria-label="Page navigation">
                    {/* Added d-flex, list-unstyled, and gap utilities to force alignment */}
                    <ul className="pagination d-flex list-unstyled align-items-center mb-0 gap-1">

                      {/* Previous Button */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded px-3 py-1"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          &laquo;
                        </button>
                      </li>

                      {/* Dynamic Page Numbers */}
                      {(() => {
                        let items = [];
                        for (let number = 1; number <= lastPage; number++) {
                          if (number === 1 || number === lastPage || Math.abs(currentPage - number) <= 1) {
                            items.push(
                              <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                <button
                                  className={`page-link rounded px-3 py-1 ${number === currentPage ? 'bg-primary text-white' : ''}`}
                                  onClick={() => handlePageChange(number)}
                                >
                                  {number}
                                </button>
                              </li>
                            );
                          } else if (number === 2 || number === lastPage - 1) {
                            items.push(
                              <li key={`ellipsis-${number}`} className="page-item disabled px-2">
                                <span className="text-muted">&hellip;</span>
                              </li>
                            );
                          }
                        }
                        return items;
                      })()}

                      {/* Next Button */}
                      <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded px-3 py-1"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === lastPage}
                          style={{ cursor: currentPage === lastPage ? 'not-allowed' : 'pointer' }}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
        </div>
      </div>
      {/* Reusable Core Modal Target */}
      <BookingViewModal ref={viewModalRef} />
    </div>
  );
}